import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  VStack,
  Text,
  Grid,
  Button,
  useBreakpointValue,
  Container,
  HStack,
  Flex,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Image,
} from "@chakra-ui/react";
import { useNavigate } from "@remix-run/react";
import useStateRef from "react-usestateref";
import userTag from "../../../assets/svgs/userTag.svg";
import wave from "../../../assets/profile/onboardingWave.svg";
import CoschoolWhite from "../../../assets/svgs/coschoolWhite.svg";
import Edit from "../../../assets/svgs/edit.svg";
import WelcomeLottie from "../../../assets/lotties/welcomeScreen.json";
import { styles, breakpoints } from "./onBoardingStyle.js";
import RESOURCES from "../../../resourses/RESOURCES";
import { isNotEmptyOrNull, urlString } from "../../../utils/common-utils";
import { getUserDetails } from "../../../api/login-service";
import { StorageService } from "../../../utils/StorageUtils";
import { postOnBoardingDetails } from "./onBoardingService";
import LottiePlayer from "../../organisms/wall/LottiePlayer";
import { getCookie } from "../../../utils/TutorConstants";
import { useRoot } from "../../../context/RootContext";
import startGif from "../../../assets/star.gif";
import userOutlined from "../../../assets/profile/user.svg";
import leftArrow from "../../../assets/svgs/arrowLeft.svg";

// Success Screen Component
const SuccessScreen = React.memo(
  ({
    userDetails,
    profileDetails,
    selectedGender,
    onEditProfile, // Add new prop for edit functionality
  }) => {
    const navigate = useNavigate();
    // Determine which avatar to display based on selected gender and avatar index
    const getAvatarImage = useCallback(() => {
      const avatarIndex = profileDetails.Avatar?.split("_")?.[1];
      const avatarImages =
        selectedGender === "boy"
          ? RESOURCES.images.three_boy
          : RESOURCES.images.three_girl;

      return avatarImages?.[avatarIndex] || userOutlined;
    }, [profileDetails.Avatar, selectedGender]);

    // Get user's personality  hobby for display
    const userHobby = profileDetails.Hobbies;

    return (
      <Box
        width="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <Text fontSize="24px" fontWeight="700" color="white" textAlign="center">
          That's awesome!
        </Text>
        <Text
          fontSize="18px"
          fontWeight="400"
          color="white"
          textAlign="center"
          mb={6}
        >
          We'll talk a lot more. For now, you've an impressive profile!
        </Text>

        {/* Profile Card */}
        <Box
          width="340px"
          height="320px"
          border="2px"
          borderColor={"white"}
          borderRadius="16px"
          position="relative"
          overflow="visible"
          mb={6}
        >
          <Box position="absolute" top="-18px" left="-26px">
            <img
              src={startGif}
              alt="star"
              style={{
                width: "70px",
                height: "70px",
                maxWidth: "100%",
                margin: "0 auto",
              }}
            />
          </Box>

          <Box position="absolute" bottom="-18px" right="-30px">
            <img
              src={startGif}
              alt="star"
              style={{
                width: "70px",
                height: "70px",
                maxWidth: "100%",
                margin: "0 auto",
              }}
            />
          </Box>

          <Flex
            direction="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            padding="4"
          >
            {/* Avatar circle - Pass the onEditProfile callback */}
            <UserAvatar
              avatarImage={getAvatarImage()}
              editIconSrc={Edit}
              onEditClick={onEditProfile}
            />

            {/* User info */}
            <Text
              color="white"
              fontSize="18px"
              fontWeight={"600"}
              lineHeight="27px"
              fontFamily={"Poppins"}
            >
              {userDetails?.firstName}
            </Text>
            <Text
              color="white"
              fontSize="14px"
              fontWeight={"400"}
              lineHeight="21px"
              fontFamily={"Poppins"}
            >
              {userDetails?.schoolGradeName + " " + userDetails?.sectionName}
            </Text>

            {isNotEmptyOrNull(userHobby) && (
              <Flex
                alignItems="center"
                marginTop={"10px"}
                borderRadius="20px"
                padding="2px 12px"
              >
                <Image
                  src={userTag}
                  alt="User tag"
                  width="20px"
                  height="20px"
                />
                <Text
                  color="white"
                  fontSize="14px"
                  paddingLeft={"6px"}
                  fontWeight={"400"}
                  lineHeight="21px"
                  fontFamily={"Poppins"}
                >
                  Loves {userHobby}
                </Text>
              </Flex>
            )}
          </Flex>
        </Box>

        <Button
          {...styles.startTour}
          onClick={() => {
            navigate(`${urlString}/home`, {
              replace: true,
              state: {
                startProductTour: true,
              },
            });
          }}
        >
          Let’s start the product tour
        </Button>
        <Button
          {...styles.skipButton}
          rightIcon={
            <Box as="span" ml={1}>
              →
            </Box>
          }
          variant="text"
          onClick={() => {
            navigate(`${urlString}/home`, {
              replace: true,
              state: {
                startProductTour: false,
              },
            });
          }}
        >
          Skip product tour
        </Button>
      </Box>
    );
  }
);

// Modified UserAvatar Component with Edit Icon Click Handler
const UserAvatar = ({ avatarImage, editIconSrc, size = 120, onEditClick }) => {
  return (
    <div
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: "100%",
        borderWidth: "1px",
        borderColor: "#7464CE",
        borderStyle: "solid",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        marginBottom: "16px",
      }}
    >
      <img
        src={avatarImage}
        alt="User Avatar"
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: "100%",
          objectFit: "cover",
        }}
      />

      <div
        style={{
          position: "absolute",
          bottom: "0",
          right: "0",
          background: "#7464CE",
          borderRadius: "100%",
          width: "30px",
          height: "30px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
        }}
        onClick={onEditClick} // Add onClick event handler
      >
        <img src={editIconSrc} alt="edit" width="16px" height="16px" />
      </div>
    </div>
  );
};

// TraitBox Component
const TraitBox = React.memo(
  ({
    item,
    index,
    isSelected,
    handleImageClick,
    currentStep,
    isMobile,
    labelData,
  }) => {
    const [isHovered, setIsHovered] = useState(false);

    const shouldShowHoverView = useMemo(
      () => isHovered || isSelected,
      [isSelected, isHovered]
    );

    const renderContent = useMemo(() => {
      if (shouldShowHoverView && (currentStep === 1 || currentStep === 2)) {
        return (
          <Text
            fontSize={isMobile ? "12px" : "14px"}
            fontWeight="medium"
            textAlign="center"
            height="100%"
            padding="2px"
            backgroundColor="#5F4DC7"
            display="flex"
            textColor="#FFFFFF"
            alignItems="center"
            borderRadius="1px"
            justifyContent="center"
          >
            {labelData?.[index]}
          </Text>
        );
      }
      return (
        <img src={item} style={styles.traitImage} alt={`Trait ${index}`} />
      );
    }, [shouldShowHoverView, currentStep, index, item, labelData, isMobile]);

    return (
      <Box
        {...(isSelected ? styles.mobileSelectedTraitBox : styles.traitBox)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => handleImageClick(index)}
      >
        {renderContent}
      </Box>
    );
  }
);

// TraitGrid Component
const TraitGrid = React.memo(
  ({
    currentStep,
    selectedGender,
    profileDetails,
    handleImageClick,
    isMobile,
    displayStep,
  }) => {
    const stepToUse = displayStep || currentStep;

    const columns = useMemo(() => {
      return stepToUse === 1
        ? isMobile
          ? 3
          : 5
        : stepToUse === 2
        ? isMobile
          ? 3
          : 4
        : isMobile
        ? 2
        : 3;
    }, [stepToUse, isMobile]);

    const getStepData = useCallback(
      (step) => {
        switch (step) {
          case 1:
            return {
              images: RESOURCES.images.one,
              labels: RESOURCES.images.one_label,
            };
          case 2:
            return {
              images: RESOURCES.images.two,
              labels: RESOURCES.images.two_label,
            };
          case 3:
            return {
              images:
                selectedGender === "boy"
                  ? RESOURCES.images.three_boy
                  : RESOURCES.images.three_girl,
              labels: null,
            };
          default:
            return { images: [], labels: [] };
        }
      },
      [selectedGender]
    );

    const stepData = useMemo(
      () => getStepData(stepToUse),
      [getStepData, stepToUse]
    );

    const getValue = useCallback(
      (step, index) => {
        if (step === 1) return RESOURCES.images.one_label?.[index];
        if (step === 2) return RESOURCES.images.two_label?.[index];
        return `${selectedGender}_${index}`;
      },
      [selectedGender]
    );

    const getSelectedValue = useCallback(
      (step) => {
        if (step === 1) return profileDetails?.Describe;
        if (step === 2) return profileDetails?.Hobbies;
        return profileDetails?.Avatar;
      },
      [profileDetails]
    );

    // Create a modified click handler that uses stepToUse instead of currentStep
    const modifiedImageClickHandler = useCallback(
      (index) => {
        handleImageClick(index, stepToUse);
      },
      [handleImageClick, stepToUse]
    );

    return (
      <Grid templateColumns={`repeat(${columns}, 0fr)`} {...styles.traitGrid}>
        {stepData.images.map((item, index) => {
          const currentValue = getSelectedValue(stepToUse);
          const itemValue = getValue(stepToUse, index);
          const isSelected = currentValue === itemValue;

          return (
            <TraitBox
              key={`${stepToUse}-${index}-${selectedGender}`}
              item={item}
              index={index}
              isSelected={isSelected}
              handleImageClick={modifiedImageClickHandler}
              currentStep={stepToUse}
              isMobile={isMobile}
              labelData={stepData.labels}
            />
          );
        })}
      </Grid>
    );
  }
);

// GenderSelector Component
const GenderSelector = React.memo(({ selectedGender, handleGenderSelect }) => (
  <Flex
    justify="center"
    w="full"
    maxW="280px"
    p={1}
    marginBottom="20px"
    borderRadius="md"
    backgroundColor="#D9D9D933"
    mx="auto"
    position="relative"
    overflow="hidden"
  >
    {/* Active background highlight that slides */}
    <Box
      position="absolute"
      height="28px"
      width="50%"
      bg="white"
      borderRadius="md"
      transform={`translateX(${selectedGender === "boy" ? "96%" : "4%"})`}
      transition="transform 0.4s ease"
      left={0}
    />

    <Button
      w="full"
      h="28px"
      p={1}
      bg="transparent"
      color={selectedGender === "girl" ? "#5F4DC7" : "white"}
      borderRadius="md"
      fontSize="md"
      onClick={() => handleGenderSelect("girl")}
      transition="all 0.4s ease"
      _hover={{
        bg: "transparent",
      }}
      position="relative"
      zIndex={1}
    >
      I'm a Girl
    </Button>

    <Button
      w="full"
      h="28px"
      p={1}
      bg="transparent"
      color={selectedGender === "boy" ? "#5F4DC7" : "white"}
      borderRadius="md"
      fontSize="md"
      onClick={() => handleGenderSelect("boy")}
      transition="all 0.4s ease"
      _hover={{
        bg: "transparent",
      }}
      position="relative"
      zIndex={1}
    >
      I'm a Boy
    </Button>
  </Flex>
));

// StepIndicator Component
const StepIndicator = React.memo(({ currentStep, setCurrentStep }) => {
  const steps = ["Step 1", "Step 2", "Step 3"];

  return (
    <HStack
      spacing={{ base: 2, md: 4 }}
      justify="center"
      flexWrap="nowrap"
      width="224px"
      px={2}
    >
      {steps.map((step, index) => (
        <Box
          key={step}
          height="4px"
          width="64px"
          borderRadius="md"
          bg={index + 1 <= currentStep ? "green.500" : "gray.300"}
          onClick={() => setCurrentStep(index + 1)}
          cursor="pointer"
        />
      ))}
    </HStack>
  );
});

// Expandable Section Component for Edit Mode
const ExpandableSection = React.memo(
  ({ step, isExpanded, onToggle, title, children }) => {
    return (
      <AccordionItem border="0" mb={4} borderRadius="md">
        <h2>
          <AccordionButton
            onClick={() => onToggle(step)}
            py={3}
            _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}
            borderRadius="md"
            color="white"
            fontFamily="Poppins"
            fontWeight="600"
            fontSize="16px"
            lineHeight="36px"
            letterSpacing="0%"
            borderBottom="1px solid rgba(255, 255, 255, 0.2)"
            _expanded={{ borderBottom: "1px solid rgba(255, 255, 255, 0.2)" }}
          >
            <Box flex="1" textAlign="left" fontWeight="600">
              {title}
            </Box>
            <AccordionIcon color="white" />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={4}>{children}</AccordionPanel>
      </AccordionItem>
    );
  }
);

// Main Component
const OnboardingScreen = ({ params }) => {
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false, lg: false });
  const [isLoading, setIsLoading] = useState(true);
  const isEdit = getCookie("isEdit") == "true";
  const { setOpenEditProfile } = useRoot();

  const [currentStep, setCurrentStep] = useState(1);
  const [userDetails, setUserDetails] = useState({});
  const [profileDetails, setProfileDetails] = useState({});
  const [selectedGender, setSelectedGender] = useState("girl");
  const [expandedIndex, setExpandedIndex, expandedIndexRef] = useStateRef(0); // Default to first section expanded
  const [showSuccessScreen, setShowSuccessScreen] = useState(false); // New state for success screen

  // Handle accordion toggle in edit mode
  const handleAccordionToggle = useCallback((index) => {
    setExpandedIndex((prevIndex) => {
      // If clicking the same item that's already expanded, close it by setting to null
      if (prevIndex === index - 1) {
        return null;
      } else {
        // Otherwise, expand the clicked item
        return index - 1;
      }
    });
  }, []);

  // Load user details
  useEffect(() => {
    let userData = StorageService.getItem("user_additional_details") || "{}";

    userData = typeof userData === "object" ? userData : JSON.parse(userData);
    setUserDetails(userData);
    setIsLoading(false);
    if (isEdit) {
      setProfileDetails(userData?.onBoardingDetails);
      setOpenEditProfile(false);
    }
  }, []);

  // Image click handler
  const handleImageClick = useCallback(
    (selectedIndex, stepOverride = null) => {
      setProfileDetails((prevProfile) => {
        const stepToUse = stepOverride || currentStep;

        const stepToKeyMap = {
          1: "Describe",
          2: "Hobbies",
          3: "Avatar",
        };

        const currentKey = stepToKeyMap[stepToUse];
        if (!currentKey) return prevProfile;

        const newValue =
          stepToUse === 1
            ? RESOURCES.images.one_label?.[selectedIndex]
            : stepToUse === 2
            ? RESOURCES.images.two_label?.[selectedIndex]
            : `${selectedGender}_${selectedIndex}`;

        return {
          ...prevProfile,
          [currentKey]: newValue,
        };
      });
    },
    [currentStep, selectedGender]
  );

  // Handle edit profile from success screen
  const handleEditProfile = useCallback(() => {
    setShowSuccessScreen(false);
    setCurrentStep(1); // Reset to step 1
  }, []);

  // Handle onboarding submission
  const onboardUser = useCallback(
    async (details) => {
      try {
        const { success, data } = await postOnBoardingDetails(details);

        if (success && data?.status === 200) {
          const { success: userSuccess, data: userData } = await getUserDetails(
            data?.response?._id,
            data?.response?.registrationStatus
          );

          if (userSuccess) {
            StorageService.setItem(
              "user_additional_details",
              JSON.stringify(userData?.data)
            );
            if (isEdit || details?.isOnBoardingSkipped === true) {
              navigate(`${urlString}/home`, {
                replace: true,
                state: {
                  startProductTour: false,
                },
              });
            } else {
              setShowSuccessScreen(true);
            }
          }
        }
      } catch (error) {
        console.error("Error during onboarding:", error);
      }
    },
    [navigate, isEdit]
  );

  // Handlers for success screen actions
  const handleStartTour = useCallback(async () => {
    try {
      // Submit onboarding details first
      const { success, data } = await postOnBoardingDetails({
        isOnBoardingSkipped: false,
        onBoardingDetails: profileDetails,
      });

      if (success && data?.status === 200) {
        const { success: userSuccess, data: userData } = await getUserDetails(
          data?.response?._id,
          data?.response?.registrationStatus
        );

        if (userSuccess) {
          StorageService.setItem(
            "user_additional_details",
            JSON.stringify(userData?.data)
          );
          // Navigate to the product tour instead of home
          navigate(`${urlString}/product-tour`, { replace: true });
        }
      }
    } catch (error) {
      console.error("Error during onboarding:", error);
    }
  }, [navigate, profileDetails]);

  const handleSkipTour = useCallback(async () => {
    try {
      // Submit onboarding details first
      const { success, data } = await postOnBoardingDetails({
        isOnBoardingSkipped: false,
        onBoardingDetails: profileDetails,
      });

      if (success && data?.status === 200) {
        const { success: userSuccess, data: userData } = await getUserDetails(
          data?.response?._id,
          data?.response?.registrationStatus
        );

        if (userSuccess) {
          StorageService.setItem(
            "user_additional_details",
            JSON.stringify(userData?.data)
          );
          // Navigate to home, skipping the tour
          navigate(`${urlString}/home`, {
            replace: true,
            state: {
              startProductTour: false,
            },
          });
        }
      }
    } catch (error) {
      console.error("Error during onboarding:", error);
    }
  }, [navigate, profileDetails]);

  // Next button handler
  const onNextClick = useCallback(() => {
    if (currentStep === 3) {
      onboardUser({
        isOnBoardingSkipped: false,
        onBoardingDetails: profileDetails,
      });
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, profileDetails, onboardUser]);

  const onBackClick = useCallback(() => {
    setCurrentStep((prev) => prev - 1);
  }, [currentStep, profileDetails, onboardUser]);

  // Skip button handler
  const onSkip = useCallback(() => {
    onboardUser({
      isOnBoardingSkipped: true,
      onBoardingDetails: {},
    });
  }, [onboardUser]);

  // Success screen content
  const renderSuccessScreen = () => {
    return (
      <SuccessScreen
        userDetails={userDetails}
        profileDetails={profileDetails}
        selectedGender={selectedGender}
        onStartTour={handleStartTour}
        onSkipTour={handleSkipTour}
        onEditProfile={handleEditProfile} // Pass the edit handler
      />
    );
  };

  // Normal content (existing code)
  const renderNormalContent = () => {
    if (isEdit) {
      return (
        <Flex justify="center" width="100%">
          <Box gap="8px" padding="20px" maxWidth="600px" width="100%">
            <Accordion
              defaultIndex={[0]}
              index={[expandedIndex]}
              allowToggle
              width="100%"
            >
              {[1, 2, 3].map((step) => (
                <ExpandableSection
                  key={`edit-step-${step}`}
                  step={step}
                  isExpanded={expandedIndex === step - 1}
                  onToggle={handleAccordionToggle}
                  title={getStepTitle(step)}
                >
                  {step === 3 && (
                    <GenderSelector
                      selectedGender={selectedGender}
                      handleGenderSelect={setSelectedGender}
                    />
                  )}
                  <TraitGrid
                    currentStep={currentStep}
                    selectedGender={selectedGender}
                    profileDetails={profileDetails}
                    handleImageClick={handleImageClick}
                    isMobile={isMobile}
                    displayStep={step}
                  />
                </ExpandableSection>
              ))}
            </Accordion>
            <Flex justify="center" mt={6}>
              <Button
                {...styles.nextButton}
                onClick={() => {
                  onboardUser({
                    isOnBoardingSkipped: false,
                    onBoardingDetails: profileDetails,
                  });
                }}
              >
                Save
              </Button>
            </Flex>
          </Box>
        </Flex>
      );
    }

    return (
      <>
        <Flex justify="center" width="100%">
          <Box
            gap="8px"
            borderRadius="16px"
            borderWidth={isMobile ? "0px" : "2px"}
            padding="20px"
            maxWidth="600px"
          >
            {getStepContent(currentStep)}
            {currentStep === 3 && (
              <GenderSelector
                selectedGender={selectedGender}
                handleGenderSelect={setSelectedGender}
              />
            )}
            <TraitGrid
              currentStep={currentStep}
              selectedGender={selectedGender}
              profileDetails={profileDetails}
              handleImageClick={handleImageClick}
              isMobile={isMobile}
            />
          </Box>
        </Flex>
        {!isMobile && (
          <StepIndicator
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        )}

        <VStack {...styles.buttonContainer}>
          <HStack>
            {currentStep !== 1 && !isMobile && (
              <Button {...styles.backButton} onClick={onBackClick}>
                {"Back"}
              </Button>
            )}
            <Button {...styles.nextButton} onClick={onNextClick}>
              {"Next"}
            </Button>
          </HStack>

          <Button
            {...styles.skipButton}
            rightIcon={
              <Box as="span" ml={1}>
                →
              </Box>
            }
            variant="text"
            onClick={onSkip}
          >
            Skip for now
          </Button>
        </VStack>
      </>
    );
  };

  // All these helper functions should still exist in your component
  // Step content
  const getStepContent = useCallback(
    (step) => {
      switch (step) {
        case 2:
          return (
            <Text {...styles.question}>Which one interests you the most?</Text>
          );
        case 3:
          return <Text {...styles.question}>Help me choose your avatar</Text>;
        default:
          return (
            <Text {...styles.question}>
              What do your friends describe you as?
            </Text>
          );
      }
    },
    [userDetails?.firstName]
  );

  // Step title for accordion
  const getStepTitle = useCallback(
    (step) => {
      switch (step) {
        case 1:
          return isEdit
            ? expandedIndexRef.current === 0
              ? "What do your friends describe you as?"
              : "Nickname"
            : "What's your friends' one-word nickname for you?";
        case 2:
          return isEdit
            ? expandedIndexRef.current === 1
              ? "Which one interests you the most?"
              : "Hobbies"
            : "Which one interests you the most?";
        case 3:
          return isEdit
            ? expandedIndexRef.current === 2
              ? "Pick your avatar!"
              : "Avatar"
            : `Let's get going, ${userDetails?.firstName}! Help me choose your avatar`;
        default:
          return "";
      }
    },
    [userDetails?.firstName, isEdit]
  );

  // Subtitle content
  const getSubtitleContent = useCallback((step, name, isMobile) => {
    switch (step) {
      case 1:
        return (
          <>
            {isMobile ? (
              <>
                I'm Vin. Let's take a moment to connect and learn a little about
                each other!
              </>
            ) : (
              <>
                I'm Vin. Let's take a moment to connect <br /> and learn a
                little about each other!
              </>
            )}
          </>
        );
      case 2:
        return <>Hey, that’s nice! Tell me about your hobbies</>;
      case 3:
        return <>Good to know! Let’s get going</>;
      default:
        return null;
    }
  }, []);

  if (isLoading) return null;

  return (
    <Box {...styles.container} height="100vh" overflow="auto">
      <img style={styles.wave} src={wave} alt="Wave" />
      {isMobile && currentStep != 1 && !showSuccessScreen && (
        <Image
          style={styles.backArrow}
          src={leftArrow}
          alt="backArrow"
          id="backArrow"
          onClick={onBackClick}
        />
      )}
      <Container {...styles.mainContent}>
        <VStack spacing={8} align="center">
          {!isMobile && (
            <img
              style={styles.bottomImg}
              src={CoschoolWhite}
              alt="Left Bottom Image"
              id="login-mobile-bottom-img"
            />
          )}

          <Box style={styles.lottieBox}>
            <LottiePlayer
              options={{
                loop: true,
                autoplay: true,
                animationData: WelcomeLottie,
                rendererSettings: {
                  preserveAspectRatio: "xMidYMid slice",
                },
              }}
              style={{
                width: "100%",
                height: "100%",
              }}
            />
          </Box>

          <VStack marginTop="-60px" spacing={2} textAlign="center">
            <div>
              {!showSuccessScreen && (currentStep === 1 || isEdit) && (
                <Text {...styles.heading}>
                  Hello, {userDetails?.firstName}!
                </Text>
              )}
              {!showSuccessScreen &&
                (isEdit ? (
                  <Text {...styles.subtext}>
                    Welcome back! Excited to update your profile?
                  </Text>
                ) : (
                  <Text {...styles.subtext}>
                    {getSubtitleContent(
                      currentStep,
                      userDetails?.firstName,
                      isMobile
                    )}
                  </Text>
                ))}
            </div>
          </VStack>

          {showSuccessScreen ? renderSuccessScreen() : renderNormalContent()}
        </VStack>
      </Container>
    </Box>
  );
};

// Include all the other components like GenderSelector, StepIndicator, ExpandableSection, etc.
// that were in your original code...

export default OnboardingScreen;
